// public/js/features/alerts/alerts-page.js
import { listAlerts, deleteAlert, updateAlert, listAlertEvents, acknowledgeEvent, metricByKey, ALERT_OPERATORS } from './api.js';
import { openAlertWizard } from './alert-wizard.js';
import { ensureNotificationPermission } from './alert-notifications.js';
import { enablePush, pushSupported } from './push-client.js';
import * as assetStore from '../assets/asset-store.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const sevBadge = (s) => `<span class="badge bg-${s === 'critical' ? 'danger' : s === 'info' ? 'secondary' : 'warning'}">${esc(s || 'warning')}</span>`;

function conditionText(a) {
  if (a.metric === 'geofence') {
    const dir = a.direction === 'both' ? 'enter & exit' : `on ${a.direction || 'both'}`;
    return `Geofence ${dir}${a.poiName ? ` — ${a.poiName}` : ''}`;
  }
  const m = metricByKey(a.metric);
  const opLabel = ALERT_OPERATORS.find(o => o.op === a.operator)?.label || a.operator;
  const range = (a.operator === 'outside' || a.operator === 'inside');
  return `${m?.label || a.metric} ${opLabel} ${a.threshold}${range && a.threshold2 != null ? `..${a.threshold2}` : ''} ${m?.unit || ''}`.trim();
}
const scopeText = (a) => a.scope === 'all' ? 'All assets' : `${(a.targets || []).length} asset(s)`;

let _assets = [];

export async function initAlertsPage(container) {
  container.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
      <h2 class="h4 mb-0">Alerts</h2>
      <div class="d-flex gap-2">
        <button id="al-enable-notif" class="btn btn-outline-secondary"><i class="fa-solid fa-bell me-2"></i>Enable notifications</button>
        <button id="al-new" class="btn btn-success"><i class="fa-solid fa-plus me-2"></i>New alert</button>
      </div>
    </div>

    <div class="card shadow-sm mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <span><i class="fa-solid fa-triangle-exclamation me-2 text-warning"></i>Active alerts <span class="badge bg-danger ms-1" id="al-active-count">0</span></span>
      </div>
      <div class="card-body p-0"><div id="al-events"></div></div>
    </div>

    <div class="card shadow-sm">
      <div class="card-header"><i class="fa-solid fa-bell me-2"></i>Alert rules <span class="badge bg-primary ms-1" id="al-rule-count">0</span></div>
      <div class="card-body p-0"><div id="al-rules"></div></div>
    </div>`;

  document.getElementById('al-enable-notif').addEventListener('click', async () => {
    // Try full Web Push (works with browser closed) first; fall back to plain
    // in-app/desktop notifications if push isn't supported or is declined.
    if (pushSupported()) {
      const r = await enablePush();
      if (r.ok) { window.showToast('Notifications + push enabled', 'success'); return; }
      if (r.reason === 'denied') { window.showToast('Notifications not granted', 'warning'); return; }
    }
    const ok = await ensureNotificationPermission(true);
    window.showToast(ok ? 'Browser notifications enabled (push unavailable)' : 'Notifications not granted', ok ? 'success' : 'warning');
  });
  document.getElementById('al-new').addEventListener('click', () => {
    openAlertWizard({ assets: _assets, onSaved: renderRules });
  });

  // assets for the wizard (best-effort) — via the shared scoped store, so the
  // picker only offers assets of the active tenant scope.
  try {
    const { assets } = await assetStore.sync();
    _assets = assets;
  } catch { _assets = []; }

  await Promise.all([renderRules(), renderEvents()]);
}

async function renderRules() {
  const wrap = document.getElementById('al-rules');
  if (!wrap) return;
  try {
    const alerts = await listAlerts();
    document.getElementById('al-rule-count').textContent = alerts.length;
    wrap.innerHTML = alerts.length ? `
      <div class="table-responsive">
      <table class="table table-hover mb-0 align-middle mobile-stack-table">
        <thead class="table-light"><tr><th>Condition</th><th>Scope</th><th>Behaviour</th><th>Severity</th><th>On</th><th></th></tr></thead>
        <tbody>${alerts.map(a => `
          <tr data-id="${a.alertId}">
            <td class="fw-semibold" data-label="Condition">${esc(conditionText(a))}</td>
            <td data-label="Scope">${esc(scopeText(a))}</td>
            <td data-label="Behaviour"><small>${esc(a.behavior || '')}</small></td>
            <td data-label="Severity">${sevBadge(a.severity)}</td>
            <td data-label="Enabled"><div class="form-check form-switch mb-0"><input class="form-check-input al-toggle" type="checkbox" ${a.enabled !== false ? 'checked' : ''}></div></td>
            <td class="text-end al-row-actions">
              <button class="btn btn-sm btn-outline-primary al-edit"><i class="fa-solid fa-pen"></i></button>
              <button class="btn btn-sm btn-outline-danger al-del"><i class="fa-solid fa-trash"></i></button>
            </td>
          </tr>`).join('')}</tbody>
      </table>
      </div>` : `<div class="text-center text-muted py-4">No alert rules yet. Click “New alert”.</div>`;

    wrap.querySelectorAll('tr[data-id]').forEach(tr => {
      const id = tr.dataset.id;
      const alert = alerts.find(a => a.alertId === id);
      tr.querySelector('.al-edit').addEventListener('click', () => openAlertWizard({ alert, assets: _assets, onSaved: renderRules }));
      tr.querySelector('.al-del').addEventListener('click', async () => {
        if (!confirm('Delete this alert?')) return;
        try { await deleteAlert(id); window.showToast('Alert deleted', 'success'); renderRules(); }
        catch (e) { window.showToast('Delete failed', 'danger'); }
      });
      tr.querySelector('.al-toggle').addEventListener('change', async (e) => {
        try { await updateAlert(id, { enabled: e.target.checked }); }
        catch (err) { window.showToast('Update failed', 'danger'); e.target.checked = !e.target.checked; }
      });
    });
  } catch (e) {
    wrap.innerHTML = `<div class="text-center text-danger py-4">Failed to load alerts: ${esc(e.message)}</div>`;
  }
}

async function renderEvents() {
  const wrap = document.getElementById('al-events');
  if (!wrap) return;
  try {
    const events = await listAlertEvents({ status: 'active', limit: 100 });
    document.getElementById('al-active-count').textContent = events.length;

    // Each event links to its asset detail page via the hash route "#assets/<uuid>"
    // (router opens the detail view). Both the asset name and an explicit button
    // are clickable so users can jump straight to the asset from an alert.
    const eventRowHtml = (ev) => {
      const href = ev.assetUUID ? `#assets/${encodeURIComponent(ev.assetUUID)}` : '';
      const label = esc(ev.assetLabel || ev.assetUUID);
      const assetCell = href
        ? `<a href="${href}" class="fw-semibold text-decoration-none" title="Open asset page">${label} <i class="fa-solid fa-arrow-up-right-from-square small ms-1"></i></a>`
        : `<span class="fw-semibold">${label}</span>`;
      const metricCell = ev.metric === 'geofence'
        ? `<b>${esc(ev.value)}</b> ${esc(ev.subLabel || 'geofence')}`
        : `${esc(metricByKey(ev.metric)?.label || ev.metric)} = <b>${esc(ev.value)}</b> (${esc(ev.operator)} ${esc(ev.threshold)})`;
      const openBtn = href
        ? `<a href="${href}" class="btn btn-sm btn-outline-primary me-1" title="Open asset page"><i class="fa-solid fa-location-crosshairs me-1"></i>Open asset</a>`
        : '';
      return `
        <tr data-id="${ev.alertEventId}">
          <td data-label="Severity">${sevBadge(ev.severity)}</td>
          <td data-label="Asset">${assetCell}</td>
          <td data-label="Trigger">${metricCell}</td>
          <td data-label="When"><small class="text-muted">${ev.triggeredAt ? new Date(ev.triggeredAt).toLocaleString() : ''}</small></td>
          <td class="text-end text-nowrap al-row-actions">${openBtn}<button class="btn btn-sm btn-outline-success al-ack"><i class="fa-solid fa-check me-1"></i>Ack</button></td>
        </tr>`;
    };

    wrap.innerHTML = events.length ? `
      <div class="table-responsive">
      <table class="table table-hover mb-0 align-middle mobile-stack-table">
        <tbody>${events.map(eventRowHtml).join('')}</tbody>
      </table>
      </div>` : `<div class="text-center text-muted py-4">No active alerts. 🎉</div>`;

    wrap.querySelectorAll('tr[data-id]').forEach(tr => {
      tr.querySelector('.al-ack').addEventListener('click', async () => {
        try { await acknowledgeEvent(tr.dataset.id); window.showToast('Acknowledged', 'success'); renderEvents(); }
        catch (e) { window.showToast('Ack failed', 'danger'); }
      });
    });
  } catch (e) {
    wrap.innerHTML = `<div class="text-center text-danger py-4">Failed to load events: ${esc(e.message)}</div>`;
  }
}
